// 🔴 P1/P2 : Composant Mandat de Gestion avec Signature Numérique
// V5.9 : Support du Plan d'Action et génération PDF
// Style Glassmorphism sombre + Néon Cyan, signature institutionnelle
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, PenTool, CheckCircle, AlertCircle, 
  FileText, Clock, Lock, Download, TrendingDown
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const MandatSignature = ({ 
  missionType, // 'demenagement' | 'succession' | 'audit'
  missionId,   // V5.9 : ID de la mission
  userData,
  formData,
  selectedActions = [], // V5.9 : Plan d'action choisi
  onSigned,
  onBack
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [mandatAccepted, setMandatAccepted] = useState(false);
  const [signing, setSigning] = useState(false);
  
  // Configuration du mandat selon le type de mission
  const mandatConfig = {
    demenagement: {
      title: "Mandat de Gestion - Déménagement",
      description: "Je mandate L'EFFACEUR pour procéder au transfert et/ou à la résiliation de mes contrats auprès des fournisseurs ci-dessous, dans le cadre de mon déménagement.",
      scope: formData?.contrats_transfert || [],
      details: `Ancienne adresse : ${formData?.ancienne_adresse}, ${formData?.ancienne_code_postal} ${formData?.ancienne_ville}\nNouvelle adresse : ${formData?.nouvelle_adresse}, ${formData?.nouvelle_code_postal} ${formData?.nouvelle_ville}\nDate de déménagement : ${formData?.date_demenagement}`
    },
    succession: {
      title: "Mandat de Gestion - Succession",
      description: "Je mandate L'EFFACEUR pour procéder à la résiliation des contrats et abonnements du défunt auprès des organismes identifiés, dans le cadre de la succession.",
      scope: formData?.organismes || [],
      details: `Défunt : ${formData?.nom_defunt}\nDate du décès : ${formData?.date_deces}\nLien de parenté : ${formData?.lien_parente}`
    },
    audit: {
      title: "Mandat de Gestion - Audit Pouvoir d'Achat",
      description: "Je mandate L'EFFACEUR pour analyser mes relevés bancaires et procéder à la résiliation des abonnements que j'aurai validés dans mon Plan d'Action.",
      scope: ["Analyse des prélèvements", "Résiliation sur validation"],
      details: "Relevés bancaires fournis : Analyse IA des abonnements récurrents"
    }
  };
  
  const config = mandatConfig[missionType] || mandatConfig.audit;
  
  // Initialiser le canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0a0f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);
  
  // Fonctions de dessin
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0a0f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };
  
  // Soumission du mandat signé
  const handleSign = async () => {
    if (!hasSignature || !cgvAccepted || !mandatAccepted) {
      toast.error('Veuillez compléter tous les champs requis');
      return;
    }
    
    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      const token = localStorage.getItem('token');
      
      // V5.9 : Envoyer le mandat signé avec le Plan d'Action et le mission_id
      const response = await axios.post(`${API}/mission-mandats/sign`, {
        mission_type: missionType,
        mission_id: missionId,
        signature_data: signatureData,
        form_data: formData,
        selected_actions: selectedActions, // V5.9 : Plan d'action validé
        cgv_accepted: cgvAccepted,
        mandat_accepted: mandatAccepted
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success('Mandat signé avec succès ! PDF généré.');
      
      // Callback vers le parent
      if (onSigned) {
        onSigned(response.data);
      }
      
    } catch (error) {
      console.error('Erreur signature:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* V1.2 : Bloc mandat simplifié — tout en un seul cadre, pas de superflu */}
      <div className="glass-card p-6 border-2 border-cyan-400/50 bg-black/80 backdrop-blur-xl">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="font-orbitron text-xl font-bold text-white">{config.title}</h2>
              <p className="text-gray-400 text-sm leading-relaxed mt-1">{config.description}</p>
            </div>
          </div>
        </div>

        {/* Garanties juridiques en une seule ligne claire */}
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-cyan-400/5 border border-cyan-400/20 text-xs">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-300 font-medium">Document juridiquement valide</span>
          </div>
          <span className="text-gray-600">•</span>
          <div className="flex items-center gap-2 text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            <span className="font-mono">Horodaté le {new Date().toLocaleString('fr-FR')}</span>
          </div>
          <span className="text-gray-600">•</span>
          <span className="text-gray-400">Art. 1984 Code civil</span>
        </div>
      </div>
      
      {/* DDD.5 : bloc "Mandats générés N fournisseurs" supprimé — créait un doublon visuel
          avec la liste des contrats déjà visible en synthèse. La liste des organismes
          est maintenant intégrée DANS le PDF du mandat lui-même (un seul mandat global). */}
      
      {/* Zone de Signature — V5.69 Premium : cadre interactif "L'Effaceur" */}
      <div className="relative">
        {/* Bordure animée néon */}
        <div className="absolute -inset-[1px] rounded-xl bg-gradient-to-r from-cyan-400/60 via-transparent to-cyan-400/20 pointer-events-none" />
        <div className="glass-card p-6 border-0 bg-black/90 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-orbitron font-bold flex items-center gap-2">
              <PenTool className="w-5 h-5 text-cyan-400" />
              Signature Numérique
            </h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/20">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-cyan-400 text-xs font-mono">L'EFFACEUR</span>
            </div>
          </div>
          
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={600}
              height={160}
              className="w-full border border-gray-700/60 rounded-lg cursor-crosshair touch-none"
              style={{ backgroundColor: '#05050a' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {!hasSignature && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-2">
                <PenTool className="w-6 h-6 text-gray-700" />
                <p className="text-gray-600 text-sm font-mono">Signez ici avec votre souris ou votre doigt</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <Button variant="ghost" onClick={clearSignature} className="text-gray-500 hover:text-white text-sm" disabled={!hasSignature}>
              Effacer
            </Button>
            {hasSignature && (
              <span className="text-cyan-400 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Signature enregistrée
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Acceptations */}
      <div className="glass-card p-6 border border-gray-800 bg-black/60 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            checked={mandatAccepted}
            onCheckedChange={setMandatAccepted}
            className="mt-1 border-cyan-400/50 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
          />
          <span className="text-gray-400 text-sm group-hover:text-white transition-colors">
            Je confirme avoir lu et accepté les termes du présent mandat de gestion. J'autorise L'EFFACEUR à agir en mon nom auprès des organismes concernés.
          </span>
        </label>
        
        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            checked={cgvAccepted}
            onCheckedChange={setCgvAccepted}
            className="mt-1 border-cyan-400/50 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
          />
          <span className="text-gray-400 text-sm group-hover:text-white transition-colors">
            J'accepte les{' '}
            <a href="/cgv" target="_blank" className="text-cyan-400 hover:underline">
              Conditions Générales de Vente
            </a>{' '}
            et la{' '}
            <a href="/confidentialite" target="_blank" className="text-cyan-400 hover:underline">
              Politique de Confidentialité
            </a>.
          </span>
        </label>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-gray-700 text-gray-400 hover:text-white"
        >
          Retour
        </Button>
        
        <Button
          onClick={handleSign}
          disabled={!hasSignature || !cgvAccepted || !mandatAccepted || signing}
          className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron px-8"
          data-testid="sign-mandat-btn"
        >
          {signing ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Signature en cours...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <PenTool className="w-4 h-4" />
              Signer le Mandat
            </span>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default MandatSignature;
